import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { Message } from '../Message';
import { Select } from '../Select';
import { Multiselect } from '../Multiselect';

import type { CategoryFiltersProps, CategoryFiltersSize, FilterCategory } from './CategoryFilters.types';

// Styled components from web-app-risk-reviews

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const Container = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[4]};
  `}
`;

const LoadingContainer = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[4]};
  `}
`;

const SkeletonLabel = styled.div`
  width: 100px;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const SkeletonSelect = styled.div`
  width: 100%;
  height: 42px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const SkeletonItem = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing[2]};
  `}
`;

const EmptyState = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing[2]};
    padding: ${theme.spacing[5]};
    text-align: center;
    background-color: ${theme.colors.background.default};
    border-radius: 8px;
  `}
`;

const EmptyIconWrapper = styled.div`
  ${({ theme }) => `
    color: ${theme.colors.text.muted};
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;

/**
 * List icon SVG for empty state - EXACT from FilterSection.tsx
 */
const ListIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="7" y1="8" x2="17" y2="8" />
    <line x1="7" y1="12" x2="17" y2="12" />
    <line x1="7" y1="16" x2="12" y2="16" />
  </svg>
);

const EmptyText = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.text.muted};
  `}
`;

/**
 * Props for CategorySelect - internal component
 */
interface CategorySelectProps {
  category: FilterCategory;
  selectedValues: string[];
  onSelect: (categoryId: string, categoryName: string, values: string[]) => void;
  disabled?: boolean;
  size?: CategoryFiltersSize;
  dataTestId?: string;
}

/**
 * Single category dropdown component using library Select/Multiselect
 * EXACT from FilterSection.tsx CategorySelect
 */
const CategorySelect: React.FC<CategorySelectProps> = ({
  category,
  selectedValues,
  onSelect,
  disabled = false,
  size = 'md',
  dataTestId,
}) => {
  const isRequired = category.required === '1';
  const isMultiple = category.multiple === '1';

  const sortedFilterValues = [...category.filterValues].sort((a, b) => a.sortOrder - b.sortOrder);

  // Convert filter values to Select/Multiselect option format
  const options = sortedFilterValues.map((filterValue) => ({
    value: filterValue.uuid,
    label: filterValue.value,
  }));

  // Handle single-select change
  const handleSingleChange = (value: string | null) => {
    if (value) {
      onSelect(category.id, category.name, [value]);
    } else {
      onSelect(category.id, category.name, []);
    }
  };

  // Handle multi-select change
  const handleMultiChange = (values: string[]) => {
    onSelect(category.id, category.name, values);
  };

  if (isMultiple) {
    return (
      <Multiselect
        label={category.name}
        required={isRequired}
        options={options}
        value={selectedValues}
        onChange={handleMultiChange}
        placeholder={`Select ${category.name.toLowerCase()}...`}
        disabled={disabled}
        size={size}
        showBulkActions
        selectAllLabel="Add All"
        clearAllLabel="Clear All"
        fullWidth
        dataTestId={dataTestId || `filter-select-${category.id}`}
      />
    );
  }

  return (
    <Select
      label={category.name}
      required={isRequired}
      options={options}
      value={selectedValues[0] ?? null}
      onChange={handleSingleChange}
      placeholder={`Select ${category.name.toLowerCase()}...`}
      disabled={disabled}
      size={size}
      fullWidth
      dataTestId={dataTestId || `filter-select-${category.id}`}
    />
  );
};

/**
 * CategoryFilters component - Displays filter category dropdowns
 *
 * EXACT copy of FilterSection styling from web-app-risk-reviews
 *
 * Displays each category as a Select (single) or Multiselect (multiple) component.
 * Required categories show orange asterisk indicator.
 *
 * @example
 * ```tsx
 * <CategoryFilters
 *   categories={categories}
 *   selectedValues={selectedValues}
 *   onSelect={handleSelect}
 *   isLoading={isLoading}
 *   loadError={loadError}
 * />
 * ```
 */
export const CategoryFilters: React.FC<CategoryFiltersProps> = ({
  categories,
  selectedValues,
  onSelect,
  isLoading = false,
  loadError,
  disabled = false,
  size = 'md',
  className,
  dataTestId = 'filter-section',
  dataId,
  style,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <Container className={className} data-testid={dataTestId} data-id={dataId} style={style}>
        <LoadingContainer data-testid="filter-loading">
          <SkeletonItem>
            <SkeletonLabel />
            <SkeletonSelect />
          </SkeletonItem>
          <SkeletonItem>
            <SkeletonLabel />
            <SkeletonSelect />
          </SkeletonItem>
        </LoadingContainer>
      </Container>
    );
  }

  // Error state - using Message component as per application pattern
  if (loadError) {
    return (
      <Container className={className} data-testid={dataTestId} data-id={dataId} style={style}>
        <Message variant="error" mode="banner" dataTestId="filter-error">
          {loadError}
        </Message>
      </Container>
    );
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <Container className={className} data-testid={dataTestId} data-id={dataId} style={style}>
        <EmptyState data-testid="filter-empty">
          <EmptyIconWrapper>
            <ListIcon />
          </EmptyIconWrapper>
          <EmptyText>No filter categories available</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  // Categories list
  return (
    <Container className={className} data-testid={dataTestId} data-id={dataId} style={style}>
      {categories.map((category) => (
        <CategorySelect
          key={category.id}
          category={category}
          selectedValues={selectedValues[category.id] || []}
          onSelect={onSelect}
          disabled={disabled}
          size={size}
        />
      ))}
    </Container>
  );
};

CategoryFilters.displayName = 'CategoryFilters';

export default CategoryFilters;
